import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, Book, Code, Terminal, GitBranch, Zap,
  FileCode, Lock, Package, Settings, ChevronRight,
  Copy, Check, ExternalLink, Search, Menu, X, Server
} from 'lucide-react';
import { Card, CardContent } from '../components/shadcn/card';
import { Button } from '../components/shadcn/button';
import { Separator } from '../components/shadcn/separator';
import { ScrollArea } from '../components/shadcn/scroll-area';

type DocSection = 'quickstart' | 'github-action' | 'api' | 'scanners' | 'configuration' | 'findings' | 'deployment';

const CodeBlock: React.FC<{ code: string; language?: string; title?: string }> = ({
  code,
  language = 'bash',
  title
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="terminal overflow-hidden">
      <div className="terminal-bar">
        <div className="terminal-dot bg-red-400/80" />
        <div className="terminal-dot bg-yellow-400/80" />
        <div className="terminal-dot bg-green-400/80" />
        <span className="ml-3 text-[11px] text-muted font-mono">{title || language}</span>
        {title && <span className="ml-auto text-xs text-muted">{language}</span>}
      </div>
      <div className="relative group">
        <pre className="p-4 overflow-x-auto text-sm">
          <code className="text-text2 font-mono">{code}</code>
        </pre>
        <button
          onClick={handleCopy}
          className="absolute top-3 right-3 p-2 rounded-lg bg-surface2 border border-border/60 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-surface3"
        >
          {copied ? (
            <Check className="w-4 h-4 text-success" />
          ) : (
            <Copy className="w-4 h-4 text-muted" />
          )}
        </button>
      </div>
    </div>
  );
};

const NavItem: React.FC<{
  section: DocSection;
  currentSection: DocSection;
  onClick: (section: DocSection) => void;
  icon: React.ReactNode;
  label: string;
}> = ({ section, currentSection, onClick, icon, label }) => (
  <Button
    variant={currentSection === section ? "default" : "ghost"}
    onClick={() => onClick(section)}
    className={`w-full justify-start gap-3 ${
      currentSection === section
        ? 'bg-brand/15 text-brand border border-brand/30 hover:bg-brand/20'
        : 'text-muted hover:text-text hover:bg-surface2/50'
    }`}
  >
    {icon}
    <span className="text-sm font-medium">{label}</span>
  </Button>
);

const Documentation: React.FC = () => {
  const [currentSection, setCurrentSection] = useState<DocSection>('quickstart');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const renderContent = () => {
    switch (currentSection) {
      case 'quickstart':
        return <QuickstartSection />;
      case 'github-action':
        return <GitHubActionSection />;
      case 'api':
        return <APISection />;
      case 'scanners':
        return <ScannersSection />;
      case 'configuration':
        return <ConfigurationSection />;
      case 'findings':
        return <FindingsSection />;
      case 'deployment':
        return <DeploymentSection />;
      default:
        return <QuickstartSection />;
    }
  };

  return (
    <div className="min-h-screen bg-bg">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-bg/80 backdrop-blur border-b border-border/30">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="Paxley" className="h-8 w-8 rounded-lg" />
              <span className="text-lg font-bold text-foreground">Paxley</span>
              <span className="text-muted mx-1">&bull;</span>
              <span className="text-muted">Documentation</span>
            </Link>

            <div className="flex items-center gap-4">
              <Button asChild className="hidden sm:flex">
                <Link to="/dashboard" className="items-center gap-2">
                  Dashboard
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-8">
          {/* Sidebar Navigation */}
          <aside className={`${mobileMenuOpen ? 'block' : 'hidden'} md:block w-full md:w-64 flex-shrink-0`}>
            <ScrollArea className="sticky top-24">
              <Card className="border-border/30 bg-surface1">
                <CardContent className="p-3">
                  <div className="text-xs font-semibold text-muted uppercase tracking-wider px-4 mb-3">
                    Getting Started
                  </div>
                  <NavItem
                    section="quickstart"
                    currentSection={currentSection}
                    onClick={(s) => { setCurrentSection(s); setMobileMenuOpen(false); }}
                    icon={<Zap className="w-4 h-4" />}
                    label="Quick Start"
                  />
                  <NavItem
                    section="github-action"
                    currentSection={currentSection}
                    onClick={(s) => { setCurrentSection(s); setMobileMenuOpen(false); }}
                    icon={<GitBranch className="w-4 h-4" />}
                    label="GitHub Action"
                  />

                  <Separator className="my-3" />

                  <div className="text-xs font-semibold text-muted uppercase tracking-wider px-4 mb-3">
                    Reference
                  </div>
                  <NavItem
                    section="api"
                    currentSection={currentSection}
                    onClick={(s) => { setCurrentSection(s); setMobileMenuOpen(false); }}
                    icon={<Code className="w-4 h-4" />}
                    label="API Reference"
                  />
                  <NavItem
                    section="scanners"
                    currentSection={currentSection}
                    onClick={(s) => { setCurrentSection(s); setMobileMenuOpen(false); }}
                    icon={<Search className="w-4 h-4" />}
                    label="Scanning Capabilities"
                  />
                  <NavItem
                    section="configuration"
                    currentSection={currentSection}
                    onClick={(s) => { setCurrentSection(s); setMobileMenuOpen(false); }}
                    icon={<Settings className="w-4 h-4" />}
                    label="Configuration"
                  />
                  <NavItem
                    section="findings"
                    currentSection={currentSection}
                    onClick={(s) => { setCurrentSection(s); setMobileMenuOpen(false); }}
                    icon={<FileCode className="w-4 h-4" />}
                    label="Understanding Findings"
                  />

                  <Separator className="my-3" />

                  <div className="text-xs font-semibold text-muted uppercase tracking-wider px-4 mb-3">
                    Deployment
                  </div>
                  <NavItem
                    section="deployment"
                    currentSection={currentSection}
                    onClick={(s) => { setCurrentSection(s); setMobileMenuOpen(false); }}
                    icon={<Server className="w-4 h-4" />}
                    label="Self-Hosted Deployment"
                  />
                </CardContent>
              </Card>
            </ScrollArea>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {renderContent()}
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-border/30 mt-16 relative z-10">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted">
            <p>&copy; 2026 Paxley.</p>
            <div className="flex items-center gap-6">
              <Link to="/terms" className="hover:text-brand transition-colors">Terms</Link>
              <Link to="/privacy" className="hover:text-brand transition-colors">Privacy</Link>
              <Link to="/status" className="hover:text-brand transition-colors">Status</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

/* ============================================
   DOCUMENTATION SECTIONS
   ============================================ */

const QuickstartSection: React.FC = () => (
  <div className="space-y-8">
    <div>
      <h1 className="text-3xl font-bold text-text mb-4">Quick Start Guide</h1>
      <p className="text-lg text-muted">
        Get comprehensive security scanning running on your repositories in under 5 minutes.
        This guide walks you through signing in, connecting your repos, running your first scan, and using AI-powered remediation.
      </p>
    </div>

    {/* Free Plan Note */}
    <Card className="bg-brand/10 border-brand/30">
      <CardContent className="p-6">
        <h3 className="text-lg font-semibold text-brand mb-2">Free to Get Started</h3>
        <p className="text-muted">
          No credit card required for the Community tier. You get 3 repositories and 5 scans per month at no cost. When your estate grows, self-hosted packages start at $199/month — priced by products and repositories, never per seat.
        </p>
      </CardContent>
    </Card>

    {/* Prerequisites */}
    <Card className="bg-medium/10 border-medium/30">
      <CardContent className="p-6">
        <h3 className="text-lg font-semibold text-medium mb-3">Prerequisites</h3>
        <ul className="space-y-2 text-muted">
          <li className="flex items-start gap-2">
            <ChevronRight className="w-4 h-4 text-medium mt-1 flex-shrink-0" />
            <span>A GitHub account with access to the repositories you want to scan</span>
          </li>
          <li className="flex items-start gap-2">
            <ChevronRight className="w-4 h-4 text-medium mt-1 flex-shrink-0" />
            <span>Organization admin or repository admin permissions (required for GitHub App installation)</span>
          </li>
        </ul>
      </CardContent>
    </Card>

    <Card className="border-border/40 bg-surface1">
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold text-text mb-4 flex items-center gap-2">
          <span className="flex items-center justify-center w-7 h-7 rounded-full bg-brand/20 text-brand text-sm font-bold">1</span>
          Sign In with GitHub
        </h2>
        <p className="text-muted mb-4">
          Go to <strong className="text-text">paxleysoftware.com</strong> and sign in with your GitHub account.
          Paxley uses GitHub OAuth for authentication — no separate account to create.
        </p>
        <Button asChild>
          <Link to="/login" className="inline-flex items-center gap-2">
            Sign in with GitHub
            <ChevronRight className="w-4 h-4" />
          </Link>
        </Button>
      </CardContent>
    </Card>

    <Card className="border-border/40 bg-surface1">
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold text-text mb-4 flex items-center gap-2">
          <span className="flex items-center justify-center w-7 h-7 rounded-full bg-brand/20 text-brand text-sm font-bold">2</span>
          Install the Paxley GitHub App
        </h2>
        <p className="text-muted mb-4">
          After signing in, you will be prompted to install the Paxley GitHub App on your organization or personal account.
          This grants Paxley read access to your source code and the ability to post PR comments.
        </p>
        <div className="bg-surface2/50 rounded-lg p-4 mb-4">
          <h4 className="text-sm font-semibold text-text mb-2">What permissions are requested?</h4>
          <ul className="text-sm text-muted space-y-1">
            <li>&bull; <strong className="text-text2">Contents (read)</strong> - Clone and analyze source code</li>
            <li>&bull; <strong className="text-text2">Pull requests (read/write)</strong> - Post scan results as PR comments</li>
            <li>&bull; <strong className="text-text2">Checks (write)</strong> - Create check runs for CI integration</li>
            <li>&bull; <strong className="text-text2">Metadata (read)</strong> - Repository information and branch data</li>
          </ul>
        </div>
      </CardContent>
    </Card>

    <Card className="border-border/40 bg-surface1">
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold text-text mb-4 flex items-center gap-2">
          <span className="flex items-center justify-center w-7 h-7 rounded-full bg-brand/20 text-brand text-sm font-bold">3</span>
          Select Repositories to Scan
        </h2>
        <p className="text-muted mb-4">
          Choose which repositories you want Paxley to scan. You can select specific repos or grant access to all repositories in the organization.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-surface2/50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-success mb-2">Recommended: Select Repositories</h4>
            <p className="text-sm text-muted">Choose specific repositories for scanning. You can add more later from Settings.</p>
          </div>
          <div className="bg-surface2/50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-muted mb-2">All Repositories</h4>
            <p className="text-sm text-muted">Automatically scan all current and future repositories in the organization.</p>
          </div>
        </div>
      </CardContent>
    </Card>

    <Card className="border-border/40 bg-surface1">
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold text-text mb-4 flex items-center gap-2">
          <span className="flex items-center justify-center w-7 h-7 rounded-full bg-brand/20 text-brand text-sm font-bold">4</span>
          Run Your First Scan
        </h2>
        <p className="text-muted mb-4">
          Click <strong className="text-text">"Run Scan"</strong> on any repository in the dashboard to kick off a full baseline scan. Scans also trigger automatically whenever you push to the default branch or open a pull request.
        </p>
        <div className="bg-surface2/50 rounded-lg p-4 mb-4">
          <h4 className="text-sm font-semibold text-text mb-2">What gets scanned?</h4>
          <div className="grid md:grid-cols-2 gap-4 text-sm text-muted">
            <div>
              <p className="font-medium text-brand mb-1">SAST (Static Analysis)</p>
              <p>Paxley Engine analyzes your code for vulnerabilities like SQL injection, XSS, and insecure deserialization.</p>
            </div>
            <div>
              <p className="font-medium text-info mb-1">Dependencies (SCA)</p>
              <p>Scans package manifests and lockfiles for known CVEs across all major package managers.</p>
            </div>
            <div>
              <p className="font-medium text-medium mb-1">Secrets</p>
              <p>Detects hardcoded API keys, tokens, passwords, and credentials in your source code.</p>
            </div>
            <div>
              <p className="font-medium text-success mb-1">Infrastructure as Code</p>
              <p>Scans Terraform, Kubernetes YAML, and Dockerfiles for misconfigurations.</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4 text-sm text-muted flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
            <span>Small repos (&lt;10K LOC): 2-3 minutes</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-medium rounded-full animate-pulse"></div>
            <span>Medium repos: 5-10 minutes</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-critical rounded-full animate-pulse"></div>
            <span>Large repos: 15-30 minutes</span>
          </div>
        </div>
      </CardContent>
    </Card>

    <Card className="border-border/40 bg-surface1">
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold text-text mb-4 flex items-center gap-2">
          <span className="flex items-center justify-center w-7 h-7 rounded-full bg-brand/20 text-brand text-sm font-bold">5</span>
          View Findings in the Dashboard
        </h2>
        <p className="text-muted mb-4">
          Once the scan completes, you will see a prioritized list of security findings in the dashboard. Findings are organized by severity and can be filtered by type, status, and file path.
        </p>
        <div className="space-y-3">
          <div className="flex items-start gap-3 bg-critical/10 border border-critical/20 rounded-lg p-3">
            <span className="px-2 py-0.5 bg-critical/20 text-critical text-xs font-bold rounded">CRITICAL</span>
            <p className="text-sm text-muted">Fix immediately. These are actively exploitable vulnerabilities with severe impact.</p>
          </div>
          <div className="flex items-start gap-3 bg-high/10 border border-high/20 rounded-lg p-3">
            <span className="px-2 py-0.5 bg-high/20 text-high text-xs font-bold rounded">HIGH</span>
            <p className="text-sm text-muted">Fix within 7 days. Significant security risks that should be prioritized.</p>
          </div>
          <div className="flex items-start gap-3 bg-medium/10 border border-amber-500/20 rounded-lg p-3">
            <span className="px-2 py-0.5 bg-medium/20 text-medium text-xs font-bold rounded">MEDIUM</span>
            <p className="text-sm text-muted">Plan to fix within 30 days. Security weaknesses that could be exploited.</p>
          </div>
          <div className="flex items-start gap-3 bg-info/10 border border-info/20 rounded-lg p-3">
            <span className="px-2 py-0.5 bg-info/20 text-info text-xs font-bold rounded">LOW</span>
            <p className="text-sm text-muted">Address when convenient. Minor issues or best practice recommendations.</p>
          </div>
        </div>
      </CardContent>
    </Card>

    <Card className="border-border/40 bg-surface1">
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold text-text mb-4 flex items-center gap-2">
          <span className="flex items-center justify-center w-7 h-7 rounded-full bg-brand/20 text-brand text-sm font-bold">6</span>
          Use AI-Powered Remediation
        </h2>
        <p className="text-muted mb-4">
          Click <strong className="text-text">"Analyze & Fix"</strong> on any finding to get AI-powered remediation suggestions.
          Paxley AI analyzes the vulnerability with full file context and generates production-ready code fixes you can apply directly.
        </p>
        <div className="bg-surface2/50 rounded-lg p-4">
          <p className="text-sm text-muted">
            Paxley AI also detects likely false positives by evaluating whether the vulnerable code path is actually reachable and whether existing mitigations are in place.
          </p>
        </div>
      </CardContent>
    </Card>

    <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-success/30">
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold text-success mb-3">You're All Set!</h2>
        <p className="text-muted mb-4">
          Paxley is now actively protecting your codebase. Here's what happens next:
        </p>
        <ul className="space-y-2 text-muted">
          <li className="flex items-start gap-2">
            <ChevronRight className="w-4 h-4 text-success mt-1 flex-shrink-0" />
            <span><strong className="text-text">Pushes and PRs</strong> are automatically scanned -- findings appear in the dashboard and as PR comments</span>
          </li>
          <li className="flex items-start gap-2">
            <ChevronRight className="w-4 h-4 text-success mt-1 flex-shrink-0" />
            <span><strong className="text-text">Fixed vulnerabilities</strong> are automatically marked as resolved on the next scan</span>
          </li>
          <li className="flex items-start gap-2">
            <ChevronRight className="w-4 h-4 text-success mt-1 flex-shrink-0" />
            <span><strong className="text-text">AI Remediation</strong> generates code fixes you can review and apply to resolve findings faster</span>
          </li>
          <li className="flex items-start gap-2">
            <ChevronRight className="w-4 h-4 text-success mt-1 flex-shrink-0" />
            <span><strong className="text-text">Dashboard</strong> tracks your security posture over time with trend metrics and export options</span>
          </li>
        </ul>
      </CardContent>
    </Card>

    {/* Common Issues */}
    <Card className="border-border/40 bg-surface1">
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold text-text mb-4">Troubleshooting</h2>
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-text2 mb-1">Scan is taking too long</h4>
            <p className="text-sm text-muted">Large repositories can take 15-30 minutes for a full baseline scan. You can disable specific scan types in Settings for faster feedback.</p>
          </div>
          <Separator />
          <div>
            <h4 className="text-sm font-semibold text-text2 mb-1">Repository not appearing</h4>
            <p className="text-sm text-muted">Ensure the Paxley GitHub App is installed on the repository. Go to GitHub Settings &rarr; Applications &rarr; Paxley &rarr; Configure to add repos.</p>
          </div>
          <Separator />
          <div>
            <h4 className="text-sm font-semibold text-text2 mb-1">Too many findings</h4>
            <p className="text-sm text-muted">Use filters to focus on Critical and High severity first. You can suppress false positives or accepted risks with a required reason and optional expiration.</p>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);

const GitHubActionSection: React.FC = () => (
  <div className="space-y-8">
    <div>
      <h1 className="text-3xl font-bold text-text mb-4">GitHub Action Integration</h1>
      <p className="text-lg text-muted">
        Integrate Paxley into your CI/CD pipeline for automated security scanning on every push and pull request.
        Paxley automatically scans PRs and posts findings as comments, with optional merge blocking for critical issues.
      </p>
    </div>

    {/* How it works */}
    <Card className="bg-brand/10 border-brand/30">
      <CardContent className="p-6">
        <h3 className="text-lg font-semibold text-brand mb-4">How PR Scanning Works</h3>
        <div className="grid md:grid-cols-4 gap-4 text-sm">
          {[
            { step: "1", text: "Developer opens PR" },
            { step: "2", text: "Paxley scans changed files only" },
            { step: "3", text: "Results posted as PR comment" },
            { step: "4", text: "Check status blocks merge if needed" },
          ].map(({ step, text }) => (
            <div key={step} className="text-center">
              <div className="w-10 h-10 bg-brand/20 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-brand font-bold">{step}</span>
              </div>
              <p className="text-muted">{text}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>

    {/* Getting API Token */}
    <Card className="border-border/40 bg-surface1">
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold text-text mb-4">Step 1: Get Your API Token</h2>
        <ol className="space-y-3 text-muted">
          {[
            <>Go to <Link to="/settings" className="text-brand hover:text-brand">Settings</Link> in the Paxley dashboard</>,
            <>Click "Generate API Token" under the API section</>,
            <>Copy the token (it won't be shown again)</>,
            <>Add it to your repository secrets as <code className="text-brand bg-surface2 px-2 py-0.5 rounded">PAXLEY_API_TOKEN</code></>,
          ].map((content, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-brand/20 text-brand rounded-full flex items-center justify-center text-sm font-bold">{i + 1}</span>
              <span>{content}</span>
            </li>
          ))}
        </ol>
        <Card className="mt-4 bg-medium/10 border-medium/30">
          <CardContent className="p-3">
            <p className="text-sm text-medium">
              <strong>Security:</strong> Never commit your API token to the repository. Always use GitHub Secrets.
            </p>
          </CardContent>
        </Card>
      </CardContent>
    </Card>

    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-text">Step 2: Create Workflow File</h2>
      <p className="text-muted">
        Create <code className="text-brand bg-surface2 px-2 py-1 rounded">.github/workflows/paxley.yml</code> in your repository:
      </p>
      <CodeBlock
        title=".github/workflows/paxley.yml"
        language="yaml"
        code={`name: Paxley Security Scan

on:
  push:
    branches: [main, master, develop]
  pull_request:
    types: [opened, synchronize, reopened]

permissions:
  contents: read
  pull-requests: write
  security-events: write

jobs:
  security-scan:
    name: Security Scan
    runs-on: ubuntu-latest
    steps:
      - name: Checkout code
        uses: actions/checkout@v4
        with:
          fetch-depth: 0  # Required for git history analysis

      - name: Run Paxley Security Scan
        uses: paxley/scan-action@v1
        with:
          api-token: \${{ secrets.PAXLEY_API_TOKEN }}
          scan-type: \${{ github.event_name == 'pull_request' && 'pr' || 'baseline' }}`}
      />
    </div>

    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-text">Configuration Options</h2>
      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60">
                  <th className="text-left py-3 px-4 text-muted font-medium">Input</th>
                  <th className="text-left py-3 px-4 text-muted font-medium">Required</th>
                  <th className="text-left py-3 px-4 text-muted font-medium">Default</th>
                  <th className="text-left py-3 px-4 text-muted font-medium">Description</th>
                </tr>
              </thead>
              <tbody className="text-text2">
                {[
                  { input: "api-token", required: true, default_: "-", desc: "Your Paxley API token from Settings" },
                  { input: "scan-type", required: false, default_: "baseline", desc: '"baseline" for full scan, "pr" for changed files only' },
                  { input: "fail-on-severity", required: false, default_: "none", desc: "Fail build if findings of this severity or higher: critical, high, medium, low" },
                  { input: "scan-types", required: false, default_: "all", desc: "Comma-separated: sast, sca, secrets, iac, license, container" },
                  { input: "upload-sarif", required: false, default_: "false", desc: "Upload SARIF to GitHub Security tab" },
                  { input: "comment-on-pr", required: false, default_: "true", desc: "Post scan results as PR comment" },
                  { input: "timeout-minutes", required: false, default_: "30", desc: "Maximum scan duration before timeout" },
                ].map(({ input, required, default_, desc }) => (
                  <tr key={input} className="border-b border-border/30">
                    <td className="py-3 px-4 font-mono text-brand">{input}</td>
                    <td className="py-3 px-4"><span className={required ? "text-critical" : ""}>{required ? "Yes" : "No"}</span></td>
                    <td className="py-3 px-4"><code className="text-muted">{default_}</code></td>
                    <td className="py-3 px-4">{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>

    {/* Common Workflows */}
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-text">Common Workflow Patterns</h2>

      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-text mb-3">Block PRs with Critical/High Findings</h3>
          <p className="text-muted mb-4 text-sm">Prevent merging if serious vulnerabilities are detected:</p>
          <CodeBlock
            language="yaml"
            code={`- name: Run Paxley Security Scan
  uses: paxley/scan-action@v1
  with:
    api-token: \${{ secrets.PAXLEY_API_TOKEN }}
    scan-type: pr
    fail-on-severity: high  # Fails on high or critical`}
          />
        </CardContent>
      </Card>

      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-text mb-3">Run Only Specific Scan Types</h3>
          <p className="text-muted mb-4 text-sm">Speed up scans by running only what you need:</p>
          <CodeBlock
            language="yaml"
            code={`- name: Quick Security Check
  uses: paxley/scan-action@v1
  with:
    api-token: \${{ secrets.PAXLEY_API_TOKEN }}
    scan-type: pr
    scan-types: secrets,sca  # Skip SAST for faster feedback`}
          />
        </CardContent>
      </Card>

      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-text mb-3">Scheduled Baseline Scans</h3>
          <p className="text-muted mb-4 text-sm">Run comprehensive scans on a schedule:</p>
          <CodeBlock
            language="yaml"
            code={`name: Nightly Security Scan

on:
  schedule:
    - cron: '0 2 * * *'  # Every night at 2 AM UTC
  workflow_dispatch:  # Allow manual trigger

jobs:
  security-scan:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
        with:
          fetch-depth: 0

      - name: Full Security Scan
        uses: paxley/scan-action@v1
        with:
          api-token: \${{ secrets.PAXLEY_API_TOKEN }}
          scan-type: baseline
          scan-types: sast,sca,secrets,iac,license,container`}
          />
        </CardContent>
      </Card>

      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-text mb-3">Integration with GitHub Security Tab</h3>
          <p className="text-muted mb-4 text-sm">Upload SARIF results to GitHub's Security tab for native integration:</p>
          <CodeBlock
            language="yaml"
            code={`- name: Run Paxley Security Scan
  uses: paxley/scan-action@v1
  with:
    api-token: \${{ secrets.PAXLEY_API_TOKEN }}
    scan-type: baseline
    upload-sarif: true  # Uploads to GitHub Security tab`}
          />
        </CardContent>
      </Card>
    </div>

    {/* Outputs */}
    <Card className="border-border/40 bg-surface1">
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold text-text mb-4">Action Outputs</h2>
        <p className="text-muted mb-4">Use these outputs in subsequent workflow steps:</p>
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/60">
                <th className="text-left py-2 px-4 text-muted font-medium">Output</th>
                <th className="text-left py-2 px-4 text-muted font-medium">Description</th>
              </tr>
            </thead>
            <tbody className="text-text2">
              {[
                { output: "findings-count", desc: "Total number of findings detected" },
                { output: "critical-count", desc: "Number of critical severity findings" },
                { output: "high-count", desc: "Number of high severity findings" },
                { output: "scan-url", desc: "URL to the scan results in Paxley dashboard" },
                { output: "sarif-file", desc: "Path to the generated SARIF file" },
              ].map(({ output, desc }) => (
                <tr key={output} className="border-b border-border/30">
                  <td className="py-2 px-4 font-mono text-brand">{output}</td>
                  <td className="py-2 px-4">{desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <CodeBlock
          language="yaml"
          title="Example: Using outputs"
          code={`- name: Run Paxley
  id: paxley
  uses: paxley/scan-action@v1
  with:
    api-token: \${{ secrets.PAXLEY_API_TOKEN }}

- name: Check results
  run: |
    echo "Found \${{ steps.paxley.outputs.findings-count }} findings"
    echo "Critical: \${{ steps.paxley.outputs.critical-count }}"
    echo "View results: \${{ steps.paxley.outputs.scan-url }}"`}
        />
      </CardContent>
    </Card>
  </div>
);

const APISection: React.FC = () => (
  <div className="space-y-8">
    <div>
      <h1 className="text-3xl font-bold text-text mb-4">API Reference</h1>
      <p className="text-lg text-muted">
        Integrate Paxley into your workflows, dashboards, and automation tools with our REST API.
        All endpoints return JSON responses and require authentication via the <code className="text-brand bg-surface2 px-1 rounded">x-paxley-key</code> header.
      </p>
    </div>

    {/* Base URL */}
    <Card className="border-border/40 bg-surface1">
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold text-text mb-4">Base URL</h2>
        <CodeBlock
          language="bash"
          code={`https://api.paxleysoftware.com/v1`}
        />
        <p className="text-muted mt-4 text-sm">
          All API endpoints are relative to this base URL. For self-hosted installations, replace with your instance URL.
        </p>
      </CardContent>
    </Card>

    {/* Authentication */}
    <Card className="border-border/40 bg-surface1">
      <CardContent className="p-6 space-y-4">
        <h2 className="text-xl font-semibold text-text">Authentication</h2>
        <p className="text-muted">
          All API requests require authentication via the <code className="text-brand bg-surface2 px-1 rounded">x-paxley-key</code> header.
        </p>
        <CodeBlock
          language="bash"
          code={`curl -H "x-paxley-key: pax_your_api_key_here" \\
     -H "Content-Type: application/json" \\
     https://api.paxleysoftware.com/v1/repos`}
        />
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div className="bg-surface2/50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-text2 mb-2">Getting Your API Key</h4>
            <ol className="text-sm text-muted space-y-1">
              <li>1. Go to Settings &rarr; API</li>
              <li>2. Click "Generate API Key"</li>
              <li>3. Copy and store securely</li>
            </ol>
          </div>
          <div className="bg-surface2/50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-text2 mb-2">Security</h4>
            <ul className="text-sm text-muted space-y-1">
              <li>&bull; Never commit API keys to source code</li>
              <li>&bull; Use environment variables or secret managers</li>
              <li>&bull; Rotate keys periodically</li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Rate Limiting */}
    <Card className="bg-medium/10 border-medium/30">
      <CardContent className="p-6">
        <h3 className="text-lg font-semibold text-medium mb-2">Rate Limiting</h3>
        <p className="text-muted text-sm mb-3">
          API requests are rate limited to ensure fair usage:
        </p>
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-text font-medium">1,000 requests/hour</p>
            <p className="text-muted">Free tier</p>
          </div>
          <div>
            <p className="text-text font-medium">10,000 requests/hour</p>
            <p className="text-muted">Pro tier</p>
          </div>
          <div>
            <p className="text-text font-medium">Unlimited</p>
            <p className="text-muted">Enterprise tier</p>
          </div>
        </div>
        <p className="text-muted text-sm mt-3">
          Rate limit headers: <code className="text-medium">X-RateLimit-Limit</code>, <code className="text-medium">X-RateLimit-Remaining</code>, <code className="text-medium">X-RateLimit-Reset</code>
        </p>
      </CardContent>
    </Card>

    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-text">Endpoints</h2>

      {/* GET /v1/repos */}
      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-2 py-1 bg-emerald-500/20 text-success text-xs font-bold rounded">GET</span>
            <code className="text-text font-mono text-lg">/v1/repos</code>
          </div>
          <p className="text-muted mb-4">List all repositories connected to your account with summary statistics.</p>

          <h4 className="text-sm font-semibold text-text2 mb-2">Example Request</h4>
          <CodeBlock
            language="bash"
            code={`curl -H "x-paxley-key: pax_your_api_key_here" \\
     https://api.paxleysoftware.com/v1/repos`}
          />

          <h4 className="text-sm font-semibold text-text2 mb-2 mt-4">Response</h4>
          <CodeBlock
            language="json"
            code={`{
  "repos": [
    {
      "id": "550e8400-e29b-41d4-a716-446655440000",
      "url": "https://github.com/acme/web-app",
      "name": "web-app",
      "owner": "acme",
      "default_branch": "main",
      "open_findings": 42,
      "critical_count": 2,
      "high_count": 8,
      "medium_count": 20,
      "low_count": 12,
      "last_scan_at": "2026-01-18T10:30:00Z",
      "last_scan_status": "ok",
      "created_at": "2025-06-15T09:00:00Z"
    }
  ],
  "total": 15,
  "limit": 50,
  "offset": 0
}`}
          />
        </CardContent>
      </Card>

      {/* GET /v1/repos/:id/findings */}
      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-2 py-1 bg-emerald-500/20 text-success text-xs font-bold rounded">GET</span>
            <code className="text-text font-mono text-lg">/v1/repos/:id/findings</code>
          </div>
          <p className="text-muted mb-4">List findings for a repository with filtering and pagination.</p>

          <h4 className="text-sm font-semibold text-text2 mb-2">Query Parameters</h4>
          <div className="overflow-x-auto mb-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60">
                  <th className="text-left py-2 px-3 text-muted">Parameter</th>
                  <th className="text-left py-2 px-3 text-muted">Type</th>
                  <th className="text-left py-2 px-3 text-muted">Description</th>
                </tr>
              </thead>
              <tbody className="text-text2">
                {[
                  { param: "severity", type: "string", desc: "Filter by severity: critical, high, medium, low (comma-separated)" },
                  { param: "status", type: "string", desc: "Filter by status: open, suppressed, fixed" },
                  { param: "tool", type: "string", desc: "Filter by scan type: sast, sca, secrets, iac, license, container" },
                  { param: "q", type: "string", desc: "Search findings by title, file path, or rule ID" },
                  { param: "limit", type: "integer", desc: "Max results per page (default: 50, max: 100)" },
                  { param: "offset", type: "integer", desc: "Pagination offset" },
                ].map(({ param, type, desc }) => (
                  <tr key={param} className="border-b border-border/30">
                    <td className="py-2 px-3 font-mono text-brand">{param}</td>
                    <td className="py-2 px-3">{type}</td>
                    <td className="py-2 px-3">{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h4 className="text-sm font-semibold text-text2 mb-2">Example Request</h4>
          <CodeBlock
            language="bash"
            code={`curl -H "x-paxley-key: pax_your_api_key_here" \\
     "https://api.paxleysoftware.com/v1/repos/REPO_ID/findings?severity=critical,high&status=open"`}
          />

          <h4 className="text-sm font-semibold text-text2 mb-2 mt-4">Response</h4>
          <CodeBlock
            language="json"
            code={`{
  "findings": [
    {
      "id": "f-123456",
      "severity": "high",
      "status": "open",
      "tool": "sast",
      "rule_id": "js/sql-injection",
      "title": "SQL Injection vulnerability",
      "description": "User input is used directly in SQL query without sanitization",
      "file_path": "src/api/users.js",
      "start_line": 42,
      "end_line": 45,
      "code_snippet": "const query = 'SELECT * FROM users WHERE id = ' + userId;",
      "cwe_id": "CWE-89",
      "cvss_score": 8.6,
      "first_seen_at": "2026-01-10T08:00:00Z",
      "last_seen_at": "2026-01-18T10:30:00Z"
    }
  ],
  "total": 42,
  "limit": 50,
  "offset": 0
}`}
          />
        </CardContent>
      </Card>

      {/* GET /v1/repos/:id/metrics */}
      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-2 py-1 bg-emerald-500/20 text-success text-xs font-bold rounded">GET</span>
            <code className="text-text font-mono text-lg">/v1/repos/:id/metrics</code>
          </div>
          <p className="text-muted mb-4">Get trend data and security metrics for a repository over time.</p>
          <CodeBlock
            language="json"
            code={`{
  "total_scans": 127,
  "findings_opened_30d": 8,
  "findings_fixed_30d": 23,
  "mean_time_to_fix_days": 4.2,
  "trend": [
    { "date": "2026-01-01", "open": 55, "fixed": 12 },
    { "date": "2026-01-08", "open": 48, "fixed": 19 },
    { "date": "2026-01-15", "open": 42, "fixed": 23 }
  ]
}`}
          />
        </CardContent>
      </Card>

      {/* GET /v1/repos/:id/export */}
      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-2 py-1 bg-emerald-500/20 text-success text-xs font-bold rounded">GET</span>
            <code className="text-text font-mono text-lg">/v1/repos/:id/export</code>
          </div>
          <p className="text-muted mb-4">Export findings in CSV or JSON format.</p>

          <h4 className="text-sm font-semibold text-text2 mb-2">Query Parameters</h4>
          <ul className="text-sm text-muted space-y-1 mb-4">
            <li><code className="text-brand">format</code> - csv or json (default: json)</li>
          </ul>

          <CodeBlock
            language="bash"
            code={`# Export as CSV
curl -H "x-paxley-key: pax_your_api_key_here" \\
     "https://api.paxleysoftware.com/v1/repos/REPO_ID/export?format=csv" \\
     -o findings.csv

# Export as JSON
curl -H "x-paxley-key: pax_your_api_key_here" \\
     "https://api.paxleysoftware.com/v1/repos/REPO_ID/export?format=json" \\
     -o findings.json`}
          />
        </CardContent>
      </Card>

      {/* POST /v1/repos/:id/scan */}
      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-2 py-1 bg-info/20 text-info text-xs font-bold rounded">POST</span>
            <code className="text-text font-mono text-lg">/v1/repos/:id/scan</code>
          </div>
          <p className="text-muted mb-4">Trigger a new scan for a repository.</p>

          <h4 className="text-sm font-semibold text-text2 mb-2">Request Body</h4>
          <CodeBlock
            language="json"
            code={`{
  "scan_type": "baseline",
  "branch": "main"
}`}
          />

          <h4 className="text-sm font-semibold text-text2 mb-2 mt-4">Response</h4>
          <CodeBlock
            language="json"
            code={`{
  "job_id": "job-789xyz",
  "status": "queued",
  "estimated_duration_seconds": 300,
  "created_at": "2026-01-18T10:30:00Z"
}`}
          />
        </CardContent>
      </Card>

      {/* GET /v1/findings/:id/remediation */}
      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-2 py-1 bg-emerald-500/20 text-success text-xs font-bold rounded">GET</span>
            <code className="text-text font-mono text-lg">/v1/findings/:id/remediation</code>
          </div>
          <p className="text-muted mb-4">Get AI-powered remediation suggestions for a specific finding. Paxley AI analyzes the finding with full file context and generates a production-ready code fix.</p>
          <CodeBlock
            language="json"
            code={`{
  "finding_id": "f-123456",
  "analysis": "This SQL injection occurs because user input is concatenated directly...",
  "suggested_fix": "const query = 'SELECT * FROM users WHERE id = $1';\\nconst result = await db.query(query, [userId]);",
  "confidence": "high",
  "false_positive_likelihood": "low"
}`}
          />
        </CardContent>
      </Card>

      {/* POST /v1/ingest */}
      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-2 py-1 bg-info/20 text-info text-xs font-bold rounded">POST</span>
            <code className="text-text font-mono text-lg">/v1/ingest</code>
          </div>
          <p className="text-muted mb-4">Ingest scan results from CI/CD pipelines. Use this endpoint to send results from the Paxley GitHub Action or custom integrations.</p>

          <CodeBlock
            language="bash"
            code={`curl -X POST \\
     -H "x-paxley-key: pax_your_api_key_here" \\
     -H "Content-Type: application/json" \\
     -d @scan-results.json \\
     https://api.paxleysoftware.com/v1/ingest`}
          />
        </CardContent>
      </Card>
    </div>

    {/* Error Handling */}
    <Card className="border-border/40 bg-surface1">
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold text-text mb-4">Error Handling</h2>
        <p className="text-muted mb-4">All errors return a consistent JSON structure:</p>
        <CodeBlock
          language="json"
          code={`{
  "error": {
    "code": "VALIDATION_ERROR",
    "message": "Invalid severity value",
    "details": {
      "field": "severity",
      "received": "super-high",
      "allowed": ["critical", "high", "medium", "low"]
    }
  }
}`}
        />
        <div className="mt-4 grid md:grid-cols-2 gap-4 text-sm">
          <div>
            <h4 className="text-text2 font-medium mb-2">HTTP Status Codes</h4>
            <ul className="text-muted space-y-1">
              <li><code className="text-success">200</code> - Success</li>
              <li><code className="text-success">201</code> - Created</li>
              <li><code className="text-medium">400</code> - Bad Request</li>
              <li><code className="text-medium">401</code> - Unauthorized</li>
              <li><code className="text-medium">403</code> - Forbidden</li>
              <li><code className="text-medium">404</code> - Not Found</li>
              <li><code className="text-critical">429</code> - Rate Limited</li>
              <li><code className="text-critical">500</code> - Server Error</li>
            </ul>
          </div>
          <div>
            <h4 className="text-text2 font-medium mb-2">Error Codes</h4>
            <ul className="text-muted space-y-1">
              <li><code className="text-brand">AUTHENTICATION_REQUIRED</code></li>
              <li><code className="text-brand">INVALID_TOKEN</code></li>
              <li><code className="text-brand">VALIDATION_ERROR</code></li>
              <li><code className="text-brand">RESOURCE_NOT_FOUND</code></li>
              <li><code className="text-brand">RATE_LIMIT_EXCEEDED</code></li>
              <li><code className="text-brand">SCAN_IN_PROGRESS</code></li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);

const ScannersSection: React.FC = () => (
  <div className="space-y-8">
    <div>
      <h1 className="text-3xl font-bold text-text mb-4">Scanning Capabilities</h1>
      <p className="text-lg text-muted">
        Paxley Engine provides comprehensive security coverage across code, dependencies, secrets, infrastructure,
        licenses, and container images. All scanning capabilities are continuously updated with the latest vulnerability
        signatures and detection rules.
      </p>
    </div>

    {/* Scanner Overview */}
    <Card className="border-border/40 bg-surface1">
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold text-text mb-4">Coverage Overview</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/60">
                <th className="text-left py-2 px-3 text-muted">Category</th>
                <th className="text-left py-2 px-3 text-muted">What It Finds</th>
                <th className="text-left py-2 px-3 text-muted">Scan Time</th>
              </tr>
            </thead>
            <tbody className="text-text2">
              {[
                { cat: "SAST", catColor: "text-brand", finds: "SQL injection, XSS, insecure crypto, hardcoded secret patterns, deserialization flaws", time: "5-30 min" },
                { cat: "SCA", catColor: "text-info", finds: "Known CVEs in npm, pip, Go, Maven, Gradle, Composer, Cargo, NuGet, Ruby gems", time: "30-60 sec" },
                { cat: "Secrets", catColor: "text-medium", finds: "Exposed API keys, tokens, passwords, certificates (redacted in findings)", time: "30-60 sec" },
                { cat: "IaC", catColor: "text-success", finds: "Misconfigurations in Terraform, Kubernetes YAML, Dockerfiles", time: "1-3 min" },
                { cat: "License", catColor: "text-emerald-400", finds: "Open-source license compliance issues (GPL, AGPL, copyleft)", time: "30 sec" },
                { cat: "Container", catColor: "text-pink-400", finds: "OS-level vulnerabilities in container images", time: "1-5 min" },
              ].map(({ cat, catColor, finds, time }) => (
                <tr key={cat} className="border-b border-border/30">
                  <td className={`py-2 px-3 font-medium ${catColor}`}>{cat}</td>
                  <td className="py-2 px-3">{finds}</td>
                  <td className="py-2 px-3">{time}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>

    <div className="grid gap-6">
      {/* SAST */}
      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-brand/20 rounded-lg">
              <FileCode className="w-5 h-5 text-brand" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text">SAST - Static Application Security Testing</h3>
              <p className="text-sm text-muted">Finds code-level vulnerabilities across your source code</p>
            </div>
          </div>
          <p className="text-muted mb-4">
            Paxley Engine analyzes your source code to find vulnerabilities including SQL injection, cross-site scripting (XSS),
            insecure cryptography, hardcoded secret patterns, insecure deserialization, and more. It understands data flow across
            functions and files, enabling detection of complex vulnerability chains.
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div className="bg-surface2/50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-text mb-2">Supported Languages</h4>
              <div className="flex flex-wrap gap-2">
                {["JavaScript", "TypeScript", "Python", "Java", "Go", "C/C++", "C#", "Ruby", "PHP"].map((lang) => (
                  <span key={lang} className="px-2 py-1 bg-brand/20 text-brand text-xs rounded">{lang}</span>
                ))}
              </div>
            </div>
            <div className="bg-surface2/50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-text mb-2">Detection Categories</h4>
              <ul className="text-sm text-muted space-y-1">
                <li>&bull; OWASP Top 10 vulnerabilities</li>
                <li>&bull; CWE Top 25 weaknesses</li>
                <li>&bull; Framework-specific issues (React, Django, Spring, Express)</li>
                <li>&bull; Authentication and authorization flaws</li>
                <li>&bull; Insecure cryptographic usage</li>
              </ul>
            </div>
          </div>

          <h4 className="text-sm font-semibold text-text mb-2">Vulnerability Categories Detected</h4>
          <div className="grid md:grid-cols-3 gap-2 text-sm">
            <div className="bg-critical/10 border border-critical/20 rounded p-2">
              <p className="font-medium text-critical">Injection Flaws</p>
              <p className="text-muted text-xs">SQL, NoSQL, Command, LDAP, XPath</p>
            </div>
            <div className="bg-high/10 border border-high/20 rounded p-2">
              <p className="font-medium text-high">XSS Variants</p>
              <p className="text-muted text-xs">Reflected, Stored, DOM-based</p>
            </div>
            <div className="bg-medium/10 border border-amber-500/20 rounded p-2">
              <p className="font-medium text-medium">Insecure Crypto</p>
              <p className="text-muted text-xs">Weak algorithms, insecure random, hardcoded keys</p>
            </div>
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded p-2">
              <p className="font-medium text-yellow-400">Path Traversal</p>
              <p className="text-muted text-xs">File read/write, Zip slip</p>
            </div>
            <div className="bg-success/10 border border-emerald-500/20 rounded p-2">
              <p className="font-medium text-success">Auth Issues</p>
              <p className="text-muted text-xs">Broken auth, session fixation, CSRF</p>
            </div>
            <div className="bg-info/10 border border-info/20 rounded p-2">
              <p className="font-medium text-info">Deserialization</p>
              <p className="text-muted text-xs">Pickle, Jackson, YAML, XML</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dependencies / SCA */}
      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-info/20 rounded-lg">
              <Package className="w-5 h-5 text-info" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text">SCA - Software Composition Analysis</h3>
              <p className="text-sm text-muted">Scans dependencies for known CVEs across all major ecosystems</p>
            </div>
          </div>
          <p className="text-muted mb-4">
            Paxley Engine scans your dependencies against multiple vulnerability databases to identify known CVEs
            in your supply chain. It supports all major package managers and provides fix version recommendations.
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div className="bg-surface2/50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-text mb-2">Supported Ecosystems</h4>
              <div className="grid grid-cols-2 gap-1 text-sm text-muted">
                <div>&bull; npm / yarn / pnpm</div>
                <div>&bull; pip / poetry / pipenv</div>
                <div>&bull; Maven / Gradle</div>
                <div>&bull; Go modules</div>
                <div>&bull; Cargo (Rust)</div>
                <div>&bull; Composer (PHP)</div>
                <div>&bull; RubyGems</div>
                <div>&bull; NuGet (.NET)</div>
              </div>
            </div>
            <div className="bg-surface2/50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-text mb-2">Enrichment Data</h4>
              <ul className="text-sm text-muted space-y-1">
                <li>&bull; <strong className="text-text">EPSS Scoring:</strong> Exploit probability in next 30 days</li>
                <li>&bull; <strong className="text-text">KEV Flagging:</strong> Known exploited vulnerabilities</li>
                <li>&bull; <strong className="text-text">CVSS:</strong> Standardized severity scoring</li>
                <li>&bull; <strong className="text-text">Fix versions:</strong> Suggested safe upgrades</li>
                <li>&bull; <strong className="text-text">Transitive deps:</strong> Full dependency tree analysis</li>
              </ul>
            </div>
          </div>

          <Card className="bg-info/10 border-blue-500/30">
            <CardContent className="p-3 text-sm">
              <p className="text-info">
                <strong>Prioritization:</strong> Findings include EPSS scores to help prioritize based on
                real-world exploitation likelihood, not just CVSS severity.
              </p>
            </CardContent>
          </Card>
        </CardContent>
      </Card>

      {/* Secrets */}
      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-medium/20 rounded-lg">
              <Lock className="w-5 h-5 text-medium" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text">Secrets Detection</h3>
              <p className="text-sm text-muted">Detects exposed API keys, tokens, passwords, and certificates</p>
            </div>
          </div>
          <p className="text-muted mb-4">
            Paxley Engine detects exposed API keys, tokens, passwords, and certificates in your source code.
            Secrets are automatically redacted in findings to prevent further exposure. Both current code and git
            history are scanned to find secrets that may have been committed and later removed.
          </p>

          <div className="grid md:grid-cols-3 gap-4 mb-4">
            {[
              { title: "Cloud Providers", color: "text-medium", items: ["AWS Access Keys", "GCP Service Accounts", "Azure Connection Strings", "DigitalOcean Tokens", "Heroku API Keys"] },
              { title: "SaaS & APIs", color: "text-medium", items: ["GitHub Tokens", "Stripe API Keys", "Twilio Credentials", "SendGrid Keys", "Slack Webhooks"] },
              { title: "Credentials", color: "text-medium", items: ["SSH Private Keys", "Database URLs", "JWT Secrets", "OAuth Tokens", "TLS Certificates"] },
            ].map(({ title, color, items }) => (
              <div key={title} className="bg-surface2/50 rounded-lg p-4">
                <h4 className={`text-sm font-semibold ${color} mb-2`}>{title}</h4>
                <ul className="text-xs text-muted space-y-1">
                  {items.map((item) => <li key={item}>&bull; {item}</li>)}
                </ul>
              </div>
            ))}
          </div>

          <Card className="bg-medium/10 border-medium/30">
            <CardContent className="p-3 text-sm">
              <p className="text-medium">
                <strong>Redaction:</strong> All detected secrets are automatically redacted in findings and reports.
                Paxley never stores or displays the full secret value.
              </p>
            </CardContent>
          </Card>
        </CardContent>
      </Card>

      {/* IaC */}
      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-emerald-500/20 rounded-lg">
              <Settings className="w-5 h-5 text-success" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text">Infrastructure as Code Scanning</h3>
              <p className="text-sm text-muted">Scans Terraform, Kubernetes YAML, and Dockerfiles for misconfigurations</p>
            </div>
          </div>
          <p className="text-muted mb-4">
            Paxley Engine scans infrastructure definitions for security misconfigurations before they reach production.
            Detects issues like public storage buckets, overly permissive security groups, unencrypted resources, and
            containers running as root.
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div className="bg-surface2/50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-text mb-2">Supported Formats</h4>
              <div className="grid grid-cols-2 gap-1 text-sm text-muted">
                {["Terraform (.tf)", "Kubernetes YAML", "Dockerfiles", "Docker Compose", "Helm Charts", "CloudFormation", "ARM Templates", "Ansible"].map((p) => (
                  <div key={p}>&bull; {p}</div>
                ))}
              </div>
            </div>
            <div className="bg-surface2/50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-text mb-2">Common Issues Detected</h4>
              <ul className="text-sm text-muted space-y-1">
                <li>&bull; Public S3 buckets / storage accounts</li>
                <li>&bull; Security groups with 0.0.0.0/0 access</li>
                <li>&bull; Unencrypted databases and volumes</li>
                <li>&bull; Missing logging and monitoring</li>
                <li>&bull; Containers running as root</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* License */}
      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-emerald-500/20 rounded-lg">
              <Shield className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text">License Compliance</h3>
              <p className="text-sm text-muted">Identifies open-source license compliance issues</p>
            </div>
          </div>
          <p className="text-muted mb-4">
            Paxley Engine identifies the licenses of all your open-source dependencies and flags potential compliance
            issues. Configure blocked licenses (e.g., GPL, AGPL) to prevent copyleft violations from entering your codebase.
          </p>
        </CardContent>
      </Card>

      {/* Container Scanning */}
      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-pink-500/20 rounded-lg">
              <Package className="w-5 h-5 text-pink-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text">Container Image Scanning</h3>
              <p className="text-sm text-muted">Scans container images for OS-level vulnerabilities</p>
            </div>
          </div>
          <p className="text-muted mb-4">
            Paxley Engine scans container images for vulnerabilities in OS packages (apt, yum, apk) and language-specific
            packages. Configure a container image in repository settings to enable automatic scanning on each build.
          </p>

          <div className="bg-surface2/50 rounded-lg p-4 mb-4">
            <h4 className="text-sm font-semibold text-text mb-2">Supported Image Sources</h4>
            <ul className="text-sm text-muted space-y-1">
              <li>&bull; Docker Hub: <code className="text-pink-400">nginx:latest</code></li>
              <li>&bull; Amazon ECR: <code className="text-pink-400">123456789.dkr.ecr.us-east-1.amazonaws.com/myapp:v1</code></li>
              <li>&bull; Google GCR: <code className="text-pink-400">gcr.io/project/image:tag</code></li>
              <li>&bull; GitHub GHCR: <code className="text-pink-400">ghcr.io/owner/image:tag</code></li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* AI Remediation */}
      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-brand/20 rounded-lg">
              <Zap className="w-5 h-5 text-brand" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text">AI-Powered Remediation</h3>
              <p className="text-sm text-muted">Production-ready code fixes generated by Paxley AI</p>
            </div>
          </div>
          <p className="text-muted mb-4">
            Paxley AI analyzes each finding with full file context and generates production-ready code fixes.
            Click "Analyze & Fix" on any finding to get an AI-generated remediation suggestion, including an
            explanation of the vulnerability, a suggested code change, and a confidence score.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-surface2/50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-text mb-2">What Paxley AI Does</h4>
              <ul className="text-sm text-muted space-y-1">
                <li>&bull; Reads the full source file for context</li>
                <li>&bull; Analyzes the vulnerability and its root cause</li>
                <li>&bull; Generates a production-ready code fix</li>
                <li>&bull; Assesses false positive likelihood</li>
              </ul>
            </div>
            <div className="bg-surface2/50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-text mb-2">Output Includes</h4>
              <ul className="text-sm text-muted space-y-1">
                <li>&bull; Plain-language vulnerability explanation</li>
                <li>&bull; Suggested code fix with diff</li>
                <li>&bull; Confidence rating (high / medium / low)</li>
                <li>&bull; False positive assessment</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
);

const ConfigurationSection: React.FC = () => (
  <div className="space-y-8">
    <div>
      <h1 className="text-3xl font-bold text-text mb-4">Configuration</h1>
      <p className="text-lg text-muted">
        Customize Paxley's behavior through the Settings page in the dashboard. Configuration can be managed
        at both the organization and individual repository level.
      </p>
    </div>

    <div className="space-y-6">
      {/* Enabled Scan Types */}
      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <h2 className="text-xl font-semibold text-text mb-4">Enabled Scan Types</h2>
          <p className="text-muted mb-4">
            Control which scan types run on each repository. Toggle individual scan types on or off from the Settings page:
          </p>
          <div className="grid md:grid-cols-3 gap-3">
            {[
              { type: "SAST", desc: "Static code analysis", color: "text-brand" },
              { type: "SCA", desc: "Dependency vulnerability scanning", color: "text-info" },
              { type: "Secrets", desc: "Credential and key detection", color: "text-medium" },
              { type: "IaC", desc: "Infrastructure misconfiguration", color: "text-success" },
              { type: "License", desc: "Open-source license compliance", color: "text-emerald-400" },
              { type: "Container", desc: "Container image vulnerabilities", color: "text-pink-400" },
            ].map(({ type, desc, color }) => (
              <div key={type} className="bg-surface2/50 rounded-lg p-3">
                <p className={`font-medium ${color} text-sm`}>{type}</p>
                <p className="text-xs text-muted">{desc}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Excluded Paths */}
      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <h2 className="text-xl font-semibold text-text mb-4">Excluded Paths</h2>
          <p className="text-muted mb-4">
            Exclude directories and files from scanning. Common patterns include vendor directories, test files, and generated code.
            Configure via the Settings page or with a <code className="text-brand bg-surface2 px-2 py-1 rounded">.paxley.yml</code> file in your repository root.
          </p>
          <CodeBlock
            title=".paxley.yml"
            language="yaml"
            code={`ignore:
  paths:
    - "vendor/**"
    - "node_modules/**"
    - "**/*.test.{js,ts}"
    - "**/*.spec.{js,ts}"
    - "**/test/**"
    - "**/tests/**"
    - "**/fixtures/**"
    - "**/__mocks__/**"
    - "dist/**"
    - "build/**"`}
          />
        </CardContent>
      </Card>

      {/* Suppression Rules */}
      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <h2 className="text-xl font-semibold text-text mb-4">Suppression Rules</h2>
          <p className="text-muted mb-4">
            Findings can be suppressed as false positives or accepted risks. Suppression policies can be configured in Settings:
          </p>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div className="bg-surface2/50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-text mb-2">Suppression Requirements</h4>
              <ul className="text-sm text-muted space-y-1">
                <li>&bull; <strong className="text-text2">Reason required:</strong> Every suppression must include a reason (false positive, accepted risk, mitigated, won't fix)</li>
                <li>&bull; <strong className="text-text2">Expiration required:</strong> Suppressions can be configured to require an expiration date</li>
                <li>&bull; <strong className="text-text2">Max duration:</strong> Set a maximum suppression duration (e.g., 90 days) to force periodic re-evaluation</li>
              </ul>
            </div>
            <div className="bg-surface2/50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-text mb-2">Inline Suppressions</h4>
              <p className="text-sm text-muted mb-2">Suppress findings directly in code:</p>
              <CodeBlock
                language="javascript"
                code={`// paxley-ignore: rule-id - Reason here
const x = unsafeOperation();`}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* PR Scanning Behavior */}
      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <h2 className="text-xl font-semibold text-text mb-4">PR Scanning Behavior</h2>
          <p className="text-muted mb-4">
            Configure how Paxley interacts with pull requests:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-surface2/50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-text mb-2">Comment Style</h4>
              <ul className="text-sm text-muted space-y-1">
                <li>&bull; <strong className="text-text2">Summary comment:</strong> Single comment with all findings</li>
                <li>&bull; <strong className="text-text2">Inline annotations:</strong> Comments on specific lines of changed code</li>
                <li>&bull; <strong className="text-text2">Both:</strong> Summary plus inline annotations</li>
              </ul>
            </div>
            <div className="bg-surface2/50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-text mb-2">Merge Blocking</h4>
              <ul className="text-sm text-muted space-y-1">
                <li>&bull; <strong className="text-text2">Block on severity:</strong> Prevent merging if findings meet a severity threshold (critical, high, medium)</li>
                <li>&bull; <strong className="text-text2">Required check:</strong> Configure as a required status check in GitHub branch protection</li>
                <li>&bull; <strong className="text-text2">Advisory only:</strong> Report findings without blocking</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <h2 className="text-xl font-semibold text-text mb-4">Notification Settings</h2>
          <p className="text-muted mb-4">
            Configure how and when you receive notifications about scan results:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { title: "Email Notifications", color: "text-brand", items: ["New critical/high findings", "Scan completion summaries", "Weekly security digest", "Suppression expiration reminders"] },
              { title: "Slack Integration", color: "text-info", items: ["Real-time alerts to a channel", "Configurable severity threshold", "Scan status updates", "Custom webhook URL"] },
            ].map(({ title, color, items }) => (
              <div key={title} className="bg-surface2/50 rounded-lg p-4">
                <h4 className={`text-sm font-semibold ${color} mb-2`}>{title}</h4>
                <ul className="text-sm text-muted space-y-1">
                  {items.map((item) => <li key={item}>&bull; {item}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Data Retention */}
      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <h2 className="text-xl font-semibold text-text mb-4">Data Retention</h2>
          <p className="text-muted mb-4">
            Paxley retains scan data and findings according to your plan:
          </p>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="bg-surface2/50 rounded-lg p-4">
              <p className="text-text font-medium mb-1">Free</p>
              <p className="text-muted">30 days of scan history</p>
            </div>
            <div className="bg-surface2/50 rounded-lg p-4">
              <p className="text-text font-medium mb-1">Pro</p>
              <p className="text-muted">1 year of scan history</p>
            </div>
            <div className="bg-surface2/50 rounded-lg p-4">
              <p className="text-text font-medium mb-1">Enterprise</p>
              <p className="text-muted">Unlimited retention, configurable</p>
            </div>
          </div>
          <p className="text-muted text-sm mt-4">
            Source code is cloned temporarily for scanning and deleted immediately after the scan completes. Paxley does not permanently store your source code.
          </p>
        </CardContent>
      </Card>
    </div>
  </div>
);

const FindingsSection: React.FC = () => (
  <div className="space-y-8">
    <div>
      <h1 className="text-3xl font-bold text-text mb-4">Understanding Findings</h1>
      <p className="text-lg text-muted">
        Learn how to interpret, prioritize, and remediate security findings. This guide covers severity levels,
        finding statuses, what each finding includes, AI-powered remediation, and export options.
      </p>
    </div>

    {/* Finding Anatomy */}
    <Card className="border-border/40 bg-surface1">
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold text-text mb-4">What Each Finding Includes</h2>
        <p className="text-muted mb-4">Every finding contains detailed information to help you understand and fix the issue:</p>
        <div className="bg-surface2/50 rounded-lg p-4 font-mono text-sm">
          <div className="space-y-2">
            <div className="flex"><span className="text-muted w-32">Title:</span><span className="text-text">SQL Injection in user query</span></div>
            <div className="flex"><span className="text-muted w-32">Severity:</span><span className="text-critical">Critical</span></div>
            <div className="flex"><span className="text-muted w-32">Location:</span><span className="text-text2">src/api/users.js:42</span></div>
            <div className="flex"><span className="text-muted w-32">Code Context:</span><span className="text-amber-300">const q = "SELECT * FROM users WHERE id=" + userId</span></div>
            <div className="flex"><span className="text-muted w-32">CWE:</span><span className="text-info">CWE-89 (SQL Injection)</span></div>
            <div className="flex"><span className="text-muted w-32">CVE:</span><span className="text-info">N/A (code-level finding)</span></div>
            <div className="flex"><span className="text-muted w-32">CVSS:</span><span className="text-critical">8.6</span></div>
            <div className="flex"><span className="text-muted w-32">EPSS:</span><span className="text-medium">0.42 (42% exploit probability)</span></div>
            <Separator className="my-2" />
            <div className="flex flex-col">
              <span className="text-muted">Description:</span>
              <span className="text-muted mt-1">User-controlled input is concatenated directly into SQL query without parameterization, allowing attackers to execute arbitrary SQL commands.</span>
            </div>
            <div className="flex flex-col mt-2">
              <span className="text-muted">Fix Recommendation:</span>
              <span className="text-success mt-1">Use parameterized queries: db.query("SELECT * FROM users WHERE id = $1", [userId])</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>

    <div className="space-y-6">
      {/* Severity Levels */}
      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <h2 className="text-xl font-semibold text-text mb-4">Severity Levels</h2>
          <p className="text-muted mb-4">Findings are classified into five severity levels based on exploitability, impact, and prevalence:</p>
          <div className="space-y-4">
            {[
              { level: "Critical", color: "critical", bgColor: "bg-critical/10", borderColor: "border-red-500/30", badgeBg: "bg-red-500/30", timeline: "Fix within 24-48 hours", desc: "Actively exploitable vulnerabilities with severe impact.", items: ["Remote code execution (RCE)", "SQL injection on sensitive data", "Hardcoded credentials with high privileges", "Authentication bypass"] },
              { level: "High", color: "high", bgColor: "bg-high/10", borderColor: "border-orange-500/30", badgeBg: "bg-orange-500/30", timeline: "Fix within 7 days", desc: "Significant security risks that could lead to compromise.", items: ["Cross-site scripting (XSS)", "Insecure deserialization", "Path traversal", "Dependencies with known exploits"] },
              { level: "Medium", color: "medium", bgColor: "bg-medium/10", borderColor: "border-medium/30", badgeBg: "bg-amber-500/30", timeline: "Fix within 30 days", desc: "Security weaknesses exploitable under certain conditions.", items: ["Information disclosure", "Missing security headers", "Weak cryptography", "CSRF vulnerabilities"] },
              { level: "Low", color: "info", bgColor: "bg-info/10", borderColor: "border-blue-500/30", badgeBg: "bg-blue-500/30", timeline: "Fix when convenient", desc: "Minor issues or defense-in-depth recommendations.", items: ["Best practice violations", "Code quality issues with security implications", "Outdated but not vulnerable dependencies"] },
              { level: "Info", color: "muted", bgColor: "bg-surface2/50", borderColor: "border-border/40", badgeBg: "bg-surface3", timeline: "Informational only", desc: "Informational findings for awareness. No immediate action required.", items: ["License information", "Dependency metadata", "Configuration observations"] },
            ].map(({ level, color, bgColor, borderColor, badgeBg, timeline, desc, items }) => (
              <div key={level} className={`${bgColor} border ${borderColor} rounded-lg p-4`}>
                <div className="flex items-center gap-3 mb-2">
                  <span className={`px-3 py-1 ${badgeBg} text-${color} text-sm font-bold rounded-full`}>{level}</span>
                  <span className="text-muted text-sm">{timeline}</span>
                </div>
                <p className="text-text2 mb-2">{desc}</p>
                <ul className="text-sm text-muted space-y-1">
                  {items.map((item) => <li key={item}>&bull; {item}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Finding Status */}
      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <h2 className="text-xl font-semibold text-text mb-4">Finding Status</h2>
          <p className="text-muted mb-4">Every finding has one of three statuses:</p>
          <div className="space-y-4">
            {[
              { status: "Open", bg: "bg-brand/20", color: "text-brand", title: "Active finding that needs attention.", desc: "Appears in reports, counts toward security metrics, and is visible in the dashboard." },
              { status: "Fixed", bg: "bg-emerald-500/20", color: "text-success", title: "Automatically resolved when the vulnerability is no longer detected on the next scan.", desc: "Tracked in metrics for historical reporting. Shows when it was first seen and when it was fixed." },
              { status: "Suppressed", bg: "bg-surface2", color: "text-muted", title: "Marked as false positive, accepted risk, mitigated, or won't fix.", desc: "Requires a reason and optional expiration date. Won't appear again unless suppression expires." },
            ].map(({ status, bg, color, title, desc }) => (
              <div key={status} className="flex items-start gap-4">
                <span className={`px-3 py-1 ${bg} ${color} text-sm font-medium rounded-full w-28 text-center flex-shrink-0`}>{status}</span>
                <div>
                  <p className="text-text2">{title}</p>
                  <p className="text-muted text-sm">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* AI Features */}
      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <h2 className="text-xl font-semibold text-text mb-4">AI-Powered Features</h2>
          <p className="text-muted mb-4">
            Paxley AI enhances every finding with intelligent analysis and remediation suggestions:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-surface2/50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-brand mb-2">Analyze & Fix</h4>
              <p className="text-sm text-muted">
                Click the <strong className="text-text">"Analyze & Fix"</strong> button on any finding to trigger Paxley AI analysis.
                It reads the full source file, understands the vulnerability context, and generates a production-ready code fix
                you can review and apply.
              </p>
            </div>
            <div className="bg-surface2/50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-info mb-2">False Positive Detection</h4>
              <p className="text-sm text-muted">
                Paxley AI evaluates whether a finding is likely a false positive by analyzing code reachability,
                existing mitigations, and contextual factors. The confidence level is shown alongside each AI analysis.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* EPSS & CVSS */}
      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <h2 className="text-xl font-semibold text-text mb-4">Prioritization Scores</h2>
          <p className="text-muted mb-4">
            Paxley enriches findings with multiple scoring systems to help prioritize remediation:
          </p>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-surface2/50 rounded-lg p-4">
              <h4 className="text-lg font-semibold text-success mb-2">EPSS Score</h4>
              <p className="text-sm text-muted mb-2">
                <strong>Exploit Prediction Scoring System</strong> - Probability that a vulnerability will be
                exploited in the next 30 days based on real-world threat intelligence.
              </p>
              <div className="text-sm">
                <div className="flex justify-between mb-1"><span className="text-muted">0.0 - 0.1</span><span className="text-success">Low risk</span></div>
                <div className="flex justify-between mb-1"><span className="text-muted">0.1 - 0.5</span><span className="text-medium">Moderate risk</span></div>
                <div className="flex justify-between"><span className="text-muted">0.5 - 1.0</span><span className="text-critical">High risk</span></div>
              </div>
            </div>

            <div className="bg-surface2/50 rounded-lg p-4">
              <h4 className="text-lg font-semibold text-info mb-2">CVSS Score</h4>
              <p className="text-sm text-muted mb-2">
                <strong>Common Vulnerability Scoring System</strong> - Standardized severity rating from 0-10
                based on vulnerability characteristics.
              </p>
              <div className="text-sm">
                <div className="flex justify-between mb-1"><span className="text-muted">0.0 - 3.9</span><span className="text-info">Low</span></div>
                <div className="flex justify-between mb-1"><span className="text-muted">4.0 - 6.9</span><span className="text-medium">Medium</span></div>
                <div className="flex justify-between mb-1"><span className="text-muted">7.0 - 8.9</span><span className="text-high">High</span></div>
                <div className="flex justify-between"><span className="text-muted">9.0 - 10.0</span><span className="text-critical">Critical</span></div>
              </div>
            </div>

            <div className="bg-surface2/50 rounded-lg p-4">
              <h4 className="text-lg font-semibold text-critical mb-2">KEV Flag</h4>
              <p className="text-sm text-muted mb-2">
                <strong>Known Exploited Vulnerabilities</strong> - CISA's catalog of vulnerabilities actively
                exploited in the wild.
              </p>
              <div className="bg-critical/10 border border-red-500/30 rounded p-2 mt-2">
                <p className="text-xs text-critical">
                  KEV-flagged vulnerabilities are being actively exploited. Prioritize these above all others.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export */}
      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <h2 className="text-xl font-semibold text-text mb-4">Exporting Findings</h2>
          <p className="text-muted mb-4">
            Export your findings data in multiple formats for reporting, compliance, and integration with other tools:
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-surface2/50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-brand mb-2">CSV Export</h4>
              <p className="text-sm text-muted">Tabular format for spreadsheets and data analysis. Includes all finding fields.</p>
            </div>
            <div className="bg-surface2/50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-info mb-2">JSON Export</h4>
              <p className="text-sm text-muted">Structured data for programmatic consumption and integration with other tools.</p>
            </div>
            <div className="bg-surface2/50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-success mb-2">HTML Security Report</h4>
              <p className="text-sm text-muted">Detailed security assessment report with executive summary, findings breakdown, and remediation guidance.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* False Positives */}
      <Card className="border-border/40 bg-surface1">
        <CardContent className="p-6">
          <h2 className="text-xl font-semibold text-text mb-4">Handling False Positives</h2>
          <p className="text-muted mb-4">
            Not every finding is a real vulnerability. Here is how to identify and handle false positives:
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div className="bg-surface2/50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-text mb-2">Common False Positive Scenarios</h4>
              <ul className="text-sm text-muted space-y-1">
                <li>&bull; Test files with intentional vulnerabilities</li>
                <li>&bull; Example/sample code not used in production</li>
                <li>&bull; Dead code that is never executed</li>
                <li>&bull; Mitigations present but not detected by static analysis</li>
                <li>&bull; Context-dependent security (e.g., admin-only internal tools)</li>
              </ul>
            </div>
            <div className="bg-surface2/50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-text mb-2">Suppression Reasons</h4>
              <ul className="text-sm text-muted space-y-1">
                <li>&bull; <strong className="text-text2">False positive:</strong> Not a real vulnerability</li>
                <li>&bull; <strong className="text-text2">Accepted risk:</strong> Known issue, accepted by team</li>
                <li>&bull; <strong className="text-text2">Won't fix:</strong> Too costly to fix, low risk</li>
                <li>&bull; <strong className="text-text2">Mitigated:</strong> Fixed by other controls</li>
              </ul>
            </div>
          </div>

          <Card className="bg-medium/10 border-medium/30">
            <CardContent className="p-4">
              <h4 className="text-sm font-semibold text-medium mb-2">Best Practices for Suppressions</h4>
              <ul className="text-sm text-muted space-y-1">
                <li>&bull; Always add a detailed comment explaining why it is suppressed</li>
                <li>&bull; Set an expiration date for "accepted risk" suppressions</li>
                <li>&bull; Review suppressions periodically (quarterly recommended)</li>
                <li>&bull; Use Paxley AI's false positive detection to validate before suppressing</li>
              </ul>
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </div>
  </div>
);

const DeploymentSection: React.FC = () => (
  <div className="space-y-8">
    <div>
      <h1 className="text-3xl font-bold text-text mb-4">Self-Hosted Deployment</h1>
      <p className="text-lg text-muted">
        Deploy Paxley on your own infrastructure using Docker Compose. Self-hosted deployment gives you full control
        over your data, network isolation, and compliance with internal security policies.
      </p>
    </div>

    {/* Prerequisites */}
    <Card className="bg-medium/10 border-medium/30">
      <CardContent className="p-6">
        <h3 className="text-lg font-semibold text-medium mb-3">Prerequisites</h3>
        <ul className="space-y-2 text-muted">
          <li className="flex items-start gap-2">
            <ChevronRight className="w-4 h-4 text-medium mt-1 flex-shrink-0" />
            <span>Docker and Docker Compose installed on the host machine</span>
          </li>
          <li className="flex items-start gap-2">
            <ChevronRight className="w-4 h-4 text-medium mt-1 flex-shrink-0" />
            <span>PostgreSQL 16 (can be run as a container or as an external managed database)</span>
          </li>
          <li className="flex items-start gap-2">
            <ChevronRight className="w-4 h-4 text-medium mt-1 flex-shrink-0" />
            <span>A GitHub App configured for your GitHub organization (for OAuth and repo access)</span>
          </li>
          <li className="flex items-start gap-2">
            <ChevronRight className="w-4 h-4 text-medium mt-1 flex-shrink-0" />
            <span>Minimum 4 GB RAM, 2 CPU cores recommended</span>
          </li>
        </ul>
      </CardContent>
    </Card>

    {/* Environment Variables */}
    <Card className="border-border/40 bg-surface1">
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold text-text mb-4">Required Environment Variables</h2>
        <p className="text-muted mb-4">
          Configure the following environment variables before starting the services. Create a <code className="text-brand bg-surface2 px-2 py-1 rounded">.env</code> file in your deployment directory:
        </p>

        <div className="overflow-x-auto mb-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/60">
                <th className="text-left py-2 px-3 text-muted">Variable</th>
                <th className="text-left py-2 px-3 text-muted">Required</th>
                <th className="text-left py-2 px-3 text-muted">Description</th>
              </tr>
            </thead>
            <tbody className="text-text2">
              {[
                { var_: "DATABASE_URL", required: true, desc: "PostgreSQL connection string (e.g., postgres://user:pass@host:5432/paxley)" },
                { var_: "GITHUB_APP_ID", required: true, desc: "GitHub App ID from your GitHub App settings" },
                { var_: "GITHUB_PRIVATE_KEY_B64", required: true, desc: "Base64-encoded GitHub App private key" },
                { var_: "GITHUB_CLIENT_ID", required: true, desc: "GitHub OAuth App client ID" },
                { var_: "GITHUB_CLIENT_SECRET", required: true, desc: "GitHub OAuth App client secret" },
                { var_: "PAXLEY_API_KEY", required: true, desc: "API key for authenticating scanner workers and API requests" },
                { var_: "COOKIE_SECRET", required: true, desc: "Secret for signing session cookies (min 32 characters)" },
                { var_: "PAYSTACK_SECRET_KEY", required: false, desc: "Paystack API key for billing integration (optional)" },
                { var_: "AZURE_OPENAI_KEY", required: false, desc: "API key for Paxley AI remediation features (optional)" },
              ].map(({ var_, required, desc }) => (
                <tr key={var_} className="border-b border-border/30">
                  <td className="py-2 px-3 font-mono text-brand">{var_}</td>
                  <td className="py-2 px-3"><span className={required ? "text-critical" : "text-muted"}>{required ? "Yes" : "Optional"}</span></td>
                  <td className="py-2 px-3">{desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Card className="bg-medium/10 border-medium/30">
          <CardContent className="p-3">
            <p className="text-sm text-medium">
              <strong>Security:</strong> Never commit your .env file to version control. Use a secrets manager in production environments.
            </p>
          </CardContent>
        </Card>
      </CardContent>
    </Card>

    {/* Docker Compose */}
    <Card className="border-border/40 bg-surface1">
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold text-text mb-4">Docker Compose Setup</h2>
        <p className="text-muted mb-4">
          Use the following <code className="text-brand bg-surface2 px-2 py-1 rounded">docker-compose.yml</code> as a reference to deploy all Paxley services:
        </p>
        <CodeBlock
          title="docker-compose.yml"
          language="yaml"
          code={`version: "3.8"

services:
  postgres:
    image: postgres:16
    restart: always
    environment:
      POSTGRES_DB: paxley
      POSTGRES_USER: paxley
      POSTGRES_PASSWORD: \${DB_PASSWORD}
    volumes:
      - pgdata:/var/lib/postgresql/data
    ports:
      - "5432:5432"

  api:
    image: paxleyacr.azurecr.io/paxley-api:latest
    restart: always
    depends_on:
      - postgres
    environment:
      DATABASE_URL: postgres://paxley:\${DB_PASSWORD}@postgres:5432/paxley
      GITHUB_APP_ID: \${GITHUB_APP_ID}
      GITHUB_PRIVATE_KEY_B64: \${GITHUB_PRIVATE_KEY_B64}
      GITHUB_CLIENT_ID: \${GITHUB_CLIENT_ID}
      GITHUB_CLIENT_SECRET: \${GITHUB_CLIENT_SECRET}
      PAXLEY_API_KEY: \${PAXLEY_API_KEY}
      COOKIE_SECRET: \${COOKIE_SECRET}
    ports:
      - "3001:3001"

  worker:
    image: paxleyacr.azurecr.io/paxley-worker:latest
    restart: always
    depends_on:
      - postgres
      - api
    environment:
      DATABASE_URL: postgres://paxley:\${DB_PASSWORD}@postgres:5432/paxley
      PAXLEY_API_KEY: \${PAXLEY_API_KEY}

  dashboard:
    image: paxleyacr.azurecr.io/paxley-dashboard:latest
    restart: always
    depends_on:
      - api
    ports:
      - "3000:80"

volumes:
  pgdata:`}
        />
      </CardContent>
    </Card>

    {/* Database Setup */}
    <Card className="border-border/40 bg-surface1">
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold text-text mb-4">Database Setup</h2>
        <p className="text-muted mb-4">
          Paxley requires PostgreSQL 16. The database schema is automatically applied on first startup via migrations.
          For production deployments, we recommend using a managed PostgreSQL service for reliability and automated backups.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-surface2/50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-text mb-2">Docker (Development)</h4>
            <p className="text-sm text-muted">
              Use the PostgreSQL container included in the Docker Compose file above. Data is persisted in a named volume.
            </p>
          </div>
          <div className="bg-surface2/50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-text mb-2">Managed (Production)</h4>
            <p className="text-sm text-muted">
              Use a managed PostgreSQL service (AWS RDS, Azure Database for PostgreSQL, Google Cloud SQL) and set the <code className="text-brand">DATABASE_URL</code> accordingly.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Starting the Services */}
    <Card className="border-border/40 bg-surface1">
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold text-text mb-4">Starting the Services</h2>
        <CodeBlock
          language="bash"
          code={`# Start all services
docker compose up -d

# Check service status
docker compose ps

# View logs
docker compose logs -f api

# Run database migrations (if needed)
docker compose exec api npm run migrate`}
        />
      </CardContent>
    </Card>

    {/* Azure Deployment Note */}
    <Card className="bg-brand/10 border-brand/30">
      <CardContent className="p-6">
        <h3 className="text-lg font-semibold text-brand mb-3">Azure Cloud Deployment</h3>
        <p className="text-muted">
          For managed Azure deployment using Azure Container Apps, Azure Database for PostgreSQL, and Azure Key Vault,
          contact <a href="mailto:yash.sancheti@paxleysoftware.com" className="text-brand hover:underline">yash.sancheti@paxleysoftware.com</a> for
          enterprise deployment support and Terraform modules.
        </p>
      </CardContent>
    </Card>
  </div>
);

export default Documentation;
